// The ticket's right-hand details (customer, order, subscriptions, what Kim did), as the ticket view
// shows them, for the views that open a ticket without being the ticket view: Review and Follow-ups.

import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { PanelRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SentinelWorkspaceAdapter } from '@/adapters/sentinelWorkspace.adapter';
import { traceHistoryFromApi } from '@/adapters/sentinelWorkspace.trace.adapter';
import { integrationService } from '@/services/integration.service';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { DetailsPanel } from './details-panel';
import { detailsPanelInputs } from './details-panel-inputs';
import { supportsCustomerTickets } from './types';

const contextQueryAtom = (conversationId: number) =>
  atomWithQuery(() => ({
    queryKey: ['sentinel-workspace', 'conversation-context', conversationId],
    queryFn: () => sentinelWorkspaceService.getConversationContext(conversationId),
    retry: false,
    refetchOnWindowFocus: false,
  }));

const integrationsQueryAtom = (brand: string | null) =>
  atomWithQuery(() => ({
    queryKey: ['sentinel-workspace', 'subscription-integrations', 'context', brand ?? 'account'],
    queryFn: async () =>
      (await integrationService.getAccountIntegrations(brand ? { bu_id: brand } : undefined)).map((i) => i.name),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  }));

/**
 * The details panel for one conversation, Sentinel ticket or not; read-only, as its actions are run
 * from the ticket view.
 */
export function TicketContextPanel({
  conversationId,
  onCollapse,
}: {
  conversationId: number;
  /** Where the panel sits inside another (Follow-ups' tabs): its collapse control hands back to that instead. */
  onCollapse?: () => void;
}) {
  // Below a wide screen the columns beside it leave no room, so it starts as its expand button.
  const [collapsed, setCollapsed] = useState(() => !onCollapse && typeof window !== 'undefined' && window.innerWidth < 1280);
  const query = useAtomValue(useMemo(() => contextQueryAtom(conversationId), [conversationId]));
  const detail = query.data?.detail;
  const sentinelTicketId = query.data?.sentinelTicketId ?? null;
  const buId = detail?.ticket.conversation.bu_id;
  const brand = buId == null || buId === '' ? null : String(buId);
  const integrations = useAtomValue(useMemo(() => integrationsQueryAtom(brand), [brand]));
  const connectedSubscriptions = integrations.isError ? undefined : (integrations.data ?? null);

  const view = useMemo(() => {
    if (!detail) return null;
    const conversation = SentinelWorkspaceAdapter.conversationFromApi(detail);
    const inputs = detailsPanelInputs({ conversation, detail, toolTrace: traceHistoryFromApi(detail), connectedSubscriptions });
    return { conversation, inputs };
  }, [detail, connectedSubscriptions]);

  if (collapsed) {
    return (
      <div className="flex shrink-0 flex-col border-l border-gray-500 bg-white p-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setCollapsed(false)}
          className="text-gray-900"
          aria-label="Show ticket details"
          title="Show ticket details"
        >
          <PanelRight className="size-[14px]" />
        </Button>
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="flex w-[340px] shrink-0 items-start border-l border-gray-500 bg-white p-4 text-[12px] text-gray-800">
        The ticket's details could not be loaded.
      </div>
    );
  }
  if (!view) {
    return (
      <div aria-busy="true" aria-label="Loading ticket details" className="flex w-[340px] shrink-0 flex-col gap-3 border-l border-gray-500 bg-white p-4">
        <Skeleton className="h-5 w-24 rounded-md" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }
  return (
    <DetailsPanel
      customer={view.conversation.customer}
      storefront={view.inputs.storefront}
      subscriptions={view.inputs.subscriptions}
      trace={view.inputs.trace}
      // The customer's other tickets are looked up through a Sentinel ticket, so only when there is one.
      customerTicketsFor={sentinelTicketId !== null && supportsCustomerTickets(detail?.ticket.source_provider) ? sentinelTicketId : undefined}
      automated={query.data?.automated}
      onCollapse={onCollapse ?? (() => setCollapsed(true))}
    />
  );
}
